import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import './Dashboard.css';

/* =========================================================
   COMMON HELPERS
========================================================= */

const getLeadName = (lead) => {
  if (!lead) return 'Unknown Lead';
  const name = `${lead.firstName || ''} ${lead.lastName || ''}`.trim();
  return name || `Lead #${lead.leadId}`;
};

const formatDuration = (seconds) => {
  if (!seconds || seconds < 1) return '0m';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours}h ${remainingMins}m`;
};

const formatTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatStatus = (status) => String(status || 'NEW').replace(/_/g, ' ');

const getStatusBadgeClass = (status) => {
  const s = String(status || 'NEW').toLowerCase().replace(/_/g, '-');
  return `badge badge-${s}`;
};

/* =========================================================
   ADMIN DASHBOARD
========================================================= */

function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadAdminDashboard = async () => {
      setLoading(true);
      setError('');
      try {
        const [leadsResponse, agentsResponse] = await Promise.all([
          api.get('/api/leads?page=0&size=100'),
          api.get('/api/users/role/AGENT'),
        ]);
        const leadsData = leadsResponse.data;
        setLeads(leadsData?.content || (Array.isArray(leadsData) ? leadsData : []));
        setTotalElements(leadsData?.totalElements || (Array.isArray(leadsData) ? leadsData.length : 0));
        setAgents(Array.isArray(agentsResponse.data) ? agentsResponse.data : []);
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.error || 'Unable to load admin dashboard.');
      } finally {
        setLoading(false);
      }
    };
    loadAdminDashboard();
  }, []);

  const statusCounts = useMemo(() => ({
    NEW: leads.filter(l => l.status === 'NEW').length,
    ASSIGNED: leads.filter(l => l.status === 'ASSIGNED').length,
    CONTACTED: leads.filter(l => l.status === 'CONTACTED').length,
    FOLLOW_UP: leads.filter(l => l.status === 'FOLLOW_UP').length,
    NOT_INTERESTED: leads.filter(l => l.status === 'NOT_INTERESTED').length,
    CONVERTED: leads.filter(l => l.status === 'CONVERTED').length,
    CLOSED: leads.filter(l => l.status === 'CLOSED').length,
  }), [leads]);

  const recentLeads = useMemo(() => leads.slice(0, 8), [leads]);
  const conversionRate = totalElements > 0 ? Math.round((statusCounts.CONVERTED / totalElements) * 100) : 0;
  const maxStatus = Math.max(...Object.values(statusCounts), 1);

  const statusRows = [
    { key: 'NEW', label: 'New', cls: 'bar-new' },
    { key: 'ASSIGNED', label: 'Assigned', cls: 'bar-assigned' },
    { key: 'CONTACTED', label: 'Contacted', cls: 'bar-contacted' },
    { key: 'FOLLOW_UP', label: 'Follow Up', cls: 'bar-follow_up' },
    { key: 'NOT_INTERESTED', label: 'Not Interested', cls: 'bar-not_interested' },
    { key: 'CONVERTED', label: 'Converted', cls: 'bar-converted' },
    { key: 'CLOSED', label: 'Closed', cls: 'bar-closed' },
  ];

  const contactedRate = totalElements > 0 ? Math.round((statusCounts.CONTACTED / totalElements) * 100) : 0;
  const followUpRate  = totalElements > 0 ? Math.round((statusCounts.FOLLOW_UP  / totalElements) * 100) : 0;
  const activeAgents  = agents.filter(a => a.status === 'ACTIVE').length;

  return (
    <div className="dashboard-wrapper">
      {/* HEADER */}
      <div className="dashboard-heading">
        <div>
          <div className="dashboard-eyebrow">Admin Workspace</div>
          <h2>Dashboard</h2>
          <p>Real-time overview of CRM leads, assignment distribution, and sales team.</p>
          <div className="dashboard-live-badge">
            <span className="live-pulse"></span>
            Live · {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
        <Link to="/leads" className="agent-start-call-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add New Lead
        </Link>
      </div>

      {error && <div className="dashboard-error">{error}</div>}

      {loading ? (
        <div className="dashboard-loading">
          <svg className="spin" style={{ marginRight: 8 }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          Loading dashboard...
        </div>
      ) : (
        <>
          {/* KPI CARDS — Rich with sub-metrics */}
          <div className="dashboard-kpi-grid">

            {/* Card 1: Total Leads */}
            <div className="dashboard-kpi-card" style={{'--kpi-color':'#4f46e5','--kpi-bg':'#eef2ff'}}>
              <div className="kpi-header">
                <div className="dashboard-kpi-icon" style={{background:'#eef2ff',color:'#4f46e5'}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-body">
                <span>Total Leads</span>
                <strong>{totalElements.toLocaleString()}</strong>
              </div>
              <div className="kpi-sub-row">
                <div className="kpi-sub-item" style={{color:'#4f46e5'}}>
                  <span className="kpi-sub-dot" style={{background:'#4f46e5'}}/>  {statusCounts.ASSIGNED} Assigned
                </div>
                <div className="kpi-sub-item" style={{color:'#94a3b8'}}>
                  <span className="kpi-sub-dot" style={{background:'#94a3b8'}}/> {statusCounts.NEW} Unassigned
                </div>
              </div>
              <div className="kpi-mini-bar-wrap">
                <div className="kpi-mini-bar" style={{width: totalElements>0?`${(statusCounts.ASSIGNED/totalElements)*100}%`:'0%', background:'linear-gradient(90deg,#4f46e5,#818cf8)'}} />
              </div>
            </div>

            {/* Card 2: Contacted */}
            <div className="dashboard-kpi-card" style={{'--kpi-color':'#06b6d4','--kpi-bg':'#ecfeff'}}>
              <div className="kpi-header">
                <div className="dashboard-kpi-icon" style={{background:'#ecfeff',color:'#06b6d4'}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <span className="kpi-change up">{contactedRate}%</span>
              </div>
              <div className="kpi-body">
                <span>Contacted Leads</span>
                <strong>{statusCounts.CONTACTED.toLocaleString()}</strong>
              </div>
              <div className="kpi-sub-row">
                <div className="kpi-sub-item" style={{color:'#0e7490'}}>
                  <span className="kpi-sub-dot" style={{background:'#06b6d4'}}/> {contactedRate}% contact rate
                </div>
              </div>
              <div className="kpi-mini-bar-wrap">
                <div className="kpi-mini-bar" style={{width:`${contactedRate}%`, background:'linear-gradient(90deg,#06b6d4,#67e8f9)'}} />
              </div>
            </div>

            {/* Card 3: Follow Ups */}
            <div className="dashboard-kpi-card" style={{'--kpi-color':'#f59e0b','--kpi-bg':'#fffbeb'}}>
              <div className="kpi-header">
                <div className="dashboard-kpi-icon" style={{background:'#fffbeb',color:'#f59e0b'}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                </div>
                {statusCounts.FOLLOW_UP > 0 && <span className="kpi-change" style={{background:'#fffbeb',color:'#b45309',border:'1px solid #fde68a'}}>Pending</span>}
              </div>
              <div className="kpi-body">
                <span>Follow Ups</span>
                <strong>{statusCounts.FOLLOW_UP.toLocaleString()}</strong>
              </div>
              <div className="kpi-sub-row">
                <div className="kpi-sub-item" style={{color:'#b45309'}}>
                  <span className="kpi-sub-dot" style={{background:'#f59e0b'}}/> {statusCounts.NOT_INTERESTED} Not interested
                </div>
              </div>
              <div className="kpi-mini-bar-wrap">
                <div className="kpi-mini-bar" style={{width: totalElements>0?`${(statusCounts.FOLLOW_UP/totalElements)*100}%`:'0%', background:'linear-gradient(90deg,#f59e0b,#fcd34d)'}} />
              </div>
            </div>

            {/* Card 4: Converted */}
            <div className="dashboard-kpi-card" style={{'--kpi-color':'#10b981','--kpi-bg':'#ecfdf5'}}>
              <div className="kpi-header">
                <div className="dashboard-kpi-icon" style={{background:'#ecfdf5',color:'#10b981'}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <span className="kpi-change up">{conversionRate}% rate</span>
              </div>
              <div className="kpi-body">
                <span>Converted</span>
                <strong>{statusCounts.CONVERTED.toLocaleString()}</strong>
              </div>
              <div className="kpi-sub-row">
                <div className="kpi-sub-item" style={{color:'#047857'}}>
                  <span className="kpi-sub-dot" style={{background:'#10b981'}}/> {statusCounts.CLOSED} Closed leads
                </div>
              </div>
              <div className="kpi-mini-bar-wrap">
                <div className="kpi-mini-bar" style={{width:`${conversionRate}%`, background:'linear-gradient(90deg,#10b981,#6ee7b7)'}} />
              </div>
            </div>

            {/* Card 5: Total Agents */}
            <div className="dashboard-kpi-card" style={{'--kpi-color':'#8b5cf6','--kpi-bg':'#f5f3ff'}}>
              <div className="kpi-header">
                <div className="dashboard-kpi-icon" style={{background:'#f5f3ff',color:'#8b5cf6'}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-body">
                <span>Total Agents</span>
                <strong>{agents.length}</strong>
              </div>
              <div className="kpi-sub-row">
                <div className="kpi-sub-item" style={{color:'#047857'}}>
                  <span className="kpi-sub-dot" style={{background:'#10b981'}}/> {activeAgents} Active
                </div>
                <div className="kpi-sub-item" style={{color:'#64748b'}}>
                  <span className="kpi-sub-dot" style={{background:'#94a3b8'}}/> {agents.length - activeAgents} Inactive
                </div>
              </div>
              <div className="kpi-mini-bar-wrap">
                <div className="kpi-mini-bar" style={{width: agents.length>0?`${(activeAgents/agents.length)*100}%`:'0%', background:'linear-gradient(90deg,#8b5cf6,#a78bfa)'}} />
              </div>
            </div>

          </div>

          {/* MAIN GRID */}
          <div className="dashboard-grid">
            {/* STATUS OVERVIEW — Interactive progress bars */}
            <section className="dashboard-card">
              <div className="dashboard-card-header">
                <div>
                  <h3>Lead Status Overview</h3>
                  <p>Distribution across pipeline stages</p>
                </div>
                <Link to="/leads">View All →</Link>
              </div>
              <div className="dashboard-status-list">
                {statusRows.map(({ key, label, cls }) => (
                  <div key={key} className="dashboard-status-row">
                    <span className="status-row-label">{label}</span>
                    <div className="status-row-bar-wrap">
                      <div
                        className={`status-row-bar ${cls}`}
                        style={{ width: `${(statusCounts[key] / maxStatus) * 100}%` }}
                      />
                    </div>
                    <span className="status-row-count">{statusCounts[key]}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* SALES TEAM ROSTER */}
            <section className="dashboard-card">
              <div className="dashboard-card-header">
                <div>
                  <h3>Sales Team</h3>
                  <p>Agents active in workspace</p>
                </div>
                <Link to="/users">Manage →</Link>
              </div>
              {agents.length === 0 ? (
                <div className="dashboard-empty">No agents registered yet.</div>
              ) : (
                <div className="dashboard-agent-list">
                  {agents.slice(0, 6).map((agent) => (
                    <div key={agent.userId} className="dashboard-agent-item">
                      <div className="dashboard-agent-avatar">
                        {(agent.fullName?.[0] || agent.username?.[0] || 'A').toUpperCase()}
                      </div>
                      <div className="dashboard-agent-info">
                        <strong>{agent.fullName || agent.username || 'Agent'}</strong>
                        <span>@{agent.username || 'agent'}</span>
                      </div>
                      <span className="dashboard-agent-role">AGENT</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* RECENT LEADS TABLE */}
          <section className="dashboard-card dashboard-recent-card">
            <div className="dashboard-card-header">
              <div>
                <h3>Recent Leads</h3>
                <p>Latest lead entries in the system</p>
              </div>
              <Link to="/leads">View All Leads →</Link>
            </div>
            {recentLeads.length === 0 ? (
              <div className="dashboard-empty">No leads found.</div>
            ) : (
              <div className="dashboard-table-wrapper">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Lead</th>
                      <th>Company</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Assigned Agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentLeads.map((lead) => (
                      <tr key={lead.leadId}>
                        <td>
                          <div className="lead-cell">
                            <div className="lead-avatar-mini">
                              {(lead.firstName?.[0] || 'L').toUpperCase()}
                            </div>
                            <Link to={`/leads/${lead.leadId}`} className="dashboard-lead-link">
                              {getLeadName(lead)}
                            </Link>
                          </div>
                        </td>
                        <td>{lead.company || '—'}</td>
                        <td>{lead.phone || '—'}</td>
                        <td>
                          <span className={getStatusBadgeClass(lead.status)}>
                            {formatStatus(lead.status)}
                          </span>
                        </td>
                        <td>{lead.assignedAgentName || <span style={{ color: 'var(--text-light)' }}>Unassigned</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/* =========================================================
   AGENT DASHBOARD
========================================================= */

function AgentDashboard({ auth }) {
  const [calls, setCalls] = useState([]);
  const [myLeads, setMyLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      setError('');
      try {
        const [callsResponse, leadsResponse] = await Promise.all([
          api.get('/api/calls/my'),
          api.get('/api/assignments/my'),
        ]);
        setCalls(Array.isArray(callsResponse.data) ? callsResponse.data : []);
        setMyLeads(Array.isArray(leadsResponse.data) ? leadsResponse.data : []);
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.error || 'Unable to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };
    loadDashboard();
  }, []);

  const todayCalls = useMemo(() => {
    const today = new Date();
    return calls.filter((call) => {
      if (!call.startTime) return false;
      const d = new Date(call.startTime);
      return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
    });
  }, [calls]);

  const connectedCalls = useMemo(() =>
    todayCalls.filter(c => ['completed', 'connected', 'answered'].includes(String(c.callStatus || '').toLowerCase())),
    [todayCalls]
  );

  const convertedCalls = useMemo(() =>
    calls.filter(c => c.disposition?.name === 'CONVERTED' || c.lead?.status === 'CONVERTED'),
    [calls]
  );

  const totalDuration = useMemo(() =>
    todayCalls.reduce((t, c) => t + (c.durationSeconds || 0), 0),
    [todayCalls]
  );

  const followUpLeads = useMemo(() =>
    myLeads.filter(a => a.lead?.status === 'FOLLOW_UP'),
    [myLeads]
  );

  const recentCalls = useMemo(() =>
    [...calls].sort((a, b) => new Date(b.startTime || 0) - new Date(a.startTime || 0)).slice(0, 8),
    [calls]
  );

  const connectRate = todayCalls.length > 0 ? Math.round((connectedCalls.length / todayCalls.length) * 100) : 0;

  const agentKpis = [
    {
      label: 'My Leads',
      value: myLeads.length,
      iconColor: '#4f46e5',
      iconBg: '#eef2ff',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
        </svg>
      ),
    },
    {
      label: 'Calls Today',
      value: todayCalls.length,
      iconColor: '#06b6d4',
      iconBg: '#ecfeff',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      ),
    },
    {
      label: 'Connected',
      value: connectedCalls.length,
      iconColor: '#10b981',
      iconBg: '#ecfdf5',
      change: `${connectRate}% rate`,
      changeType: 'up',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ),
    },
    {
      label: 'Converted',
      value: convertedCalls.length,
      iconColor: '#f59e0b',
      iconBg: '#fffbeb',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
        </svg>
      ),
    },
  ];

  return (
    <div className="agent-dashboard">
      {/* HEADER */}
      <div className="agent-dashboard-header">
        <div>
          <div className="agent-dashboard-eyebrow">Agent Workstation</div>
          <h2>Hello, {auth?.fullName || auth?.username || 'Agent'} 👋</h2>
          <p>Track your lead assignments and daily outbound calling queue.</p>
          <div className="dashboard-live-badge">
            <span className="live-pulse"></span>
            Live · {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
        <Link to="/dialer" className="agent-start-call-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
          <span>Start Calling</span>
        </Link>
      </div>

      {error && <div className="dashboard-error">{error}</div>}

      {loading ? (
        <div className="dashboard-loading">Loading your dashboard...</div>
      ) : (
        <>
          {/* KPI GRID */}
          <div className="agent-kpi-grid">
            {agentKpis.map((kpi, i) => (
              <div
                key={i}
                className="agent-kpi-card"
                style={{ '--kpi-color': kpi.iconColor, '--kpi-bg': kpi.iconBg }}
              >
                <div className="kpi-header">
                  <div className="agent-kpi-icon" style={{ background: kpi.iconBg, color: kpi.iconColor }}>
                    {kpi.icon}
                  </div>
                  {kpi.change && (
                    <span className={`kpi-change ${kpi.changeType || 'up'}`}>{kpi.change}</span>
                  )}
                </div>
                <div className="kpi-body">
                  <span>{kpi.label}</span>
                  <strong>{kpi.value}</strong>
                </div>
              </div>
            ))}
          </div>

          {/* CALLING + FOLLOW UPS */}
          <div className="agent-dashboard-grid">
            <section className="agent-card">
              <div className="agent-card-header">
                <div>
                  <h3>Today's Activity</h3>
                  <p>Summary of calls placed today</p>
                </div>
                <Link to="/dialer">Open Dialer →</Link>
              </div>
              <div className="calling-summary">
                <div className="calling-summary-item">
                  <span>Total Calls</span>
                  <strong>{todayCalls.length}</strong>
                </div>
                <div className="calling-summary-item">
                  <span>Connected</span>
                  <strong>{connectedCalls.length}</strong>
                </div>
                <div className="calling-summary-item">
                  <span>Talk Time</span>
                  <strong>{formatDuration(totalDuration)}</strong>
                </div>
              </div>
            </section>

            <section className="agent-card">
              <div className="agent-card-header">
                <div>
                  <h3>Follow-up Leads</h3>
                  <p>Leads flagged for follow-up call</p>
                </div>
                <Link to="/myleads">View All →</Link>
              </div>
              {followUpLeads.length === 0 ? (
                <div className="agent-empty">No follow-up leads currently scheduled.</div>
              ) : (
                <div className="followup-list">
                  {followUpLeads.slice(0, 5).map((assignment) => {
                    const lead = assignment.lead;
                    return (
                      <Link key={assignment.assignmentId} to={`/leads/${lead?.leadId}`} className="followup-item">
                        <div>
                          <strong>{getLeadName(lead)}</strong>
                          <span>{lead?.company || lead?.phone || '—'}</span>
                        </div>
                        <span className="badge badge-follow-up">FOLLOW UP</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          {/* RECENT CALLS */}
          <section className="agent-card recent-calls-card">
            <div className="agent-card-header">
              <div>
                <h3>Recent Calls</h3>
                <p>Your latest call history</p>
              </div>
              <Link to="/dialer">Open Dialer →</Link>
            </div>
            {recentCalls.length === 0 ? (
              <div className="agent-empty">No calls logged yet.</div>
            ) : (
              <div className="agent-table-wrapper">
                <table className="agent-table">
                  <thead>
                    <tr>
                      <th>Lead</th>
                      <th>Number</th>
                      <th>Status</th>
                      <th>Disposition</th>
                      <th>Duration</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentCalls.map((call) => (
                      <tr key={call.callId}>
                        <td>
                          <div className="lead-cell">
                            <div className="lead-avatar-mini">
                              {(call.lead?.firstName?.[0] || 'L').toUpperCase()}
                            </div>
                            <Link to={`/leads/${call.lead?.leadId}`} className="agent-lead-link">
                              {getLeadName(call.lead)}
                            </Link>
                          </div>
                        </td>
                        <td>{call.toNumber || call.lead?.phone || '—'}</td>
                        <td>
                          <span className="badge badge-assigned">{call.callStatus || 'Logged'}</span>
                        </td>
                        <td>{call.disposition?.name || '—'}</td>
                        <td style={{ fontFamily: 'monospace' }}>{formatDuration(call.durationSeconds)}</td>
                        <td>{formatTime(call.startTime)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default function Dashboard({ auth }) {
  if (auth?.role === 'ADMIN' || auth?.role === 'MANAGER') {
    return <AdminDashboard />;
  }
  return <AgentDashboard auth={auth} />;
}